import { useToast } from "@flat-ui";
import { useAppDispatch } from "@store/store-helper";
import { resetSplashScreenStatus } from "@store/user/user-slice";
import { setFueFlags } from "@store/user/user-slice-thunk";
import { tryToClearCookies } from "@utils/cookie-utils";
import { ButtonEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";
import {
  DevToolKey,
  TEST_ERROR_MESSAGE,
} from "@components/dev-features-dialog/features";
import { sentryCaptureError } from "@utils/sentry-utils";
import { copyToClipboard } from "@utils/ui-utils";
import { generateGUID } from "@faro-lotv/foundation";
import { isLocalhost } from "@faro-lotv/foundation";
import {
  TogglesCallback,
  ButtonsCallback,
} from "@components/dev-features-dialog/features";
import {
  setIsTranslationOverrideAllowed,
  setIsDarkModeEnabled,
  setIsSentryEnabled,
} from "@store/dev-features/dev-features-slice";
import { useFeatureToggle } from "@components/dev-features-dialog/use-feature-toggle";
import { useCompanyId } from "@context-providers/company-id-provider";
import { runtimeConfig } from "@src/runtime-config";
import { getBooleanQueryParam } from "@utils/url-utils";
import { QueryParams } from "@router/route-params";

/** Type definition for the return value of the useDevTools hook */
type UseDevToolsProps = Record<DevToolKey, ButtonsCallback | TogglesCallback>;

/**
 * Custom hook that provides development tool actions.
 */
export function useDevTools(): UseDevToolsProps {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { updateSearchParams } = useFeatureToggle();
  const companyId = useCompanyId();

  /**
   * Opens the admin panel in a new tab with the current workspace.
   */
  const openAdminPanel = useCallback(() => {
    const adminPanelUrl = runtimeConfig.urls.adminPanelUrl;
    const tabName = "AdminPanel";
    window.open(`${adminPanelUrl}/company/${companyId}`, tabName);
  }, [companyId]);

  /**
   * Try our best to clear all browser-accessible cookies.
   * https://github.com/js-cookie/js-cookie/wiki/Frequently-Asked-Questions#how-to-remove-all-cookies
   */
  const clearCookies = useCallback(() => {
    const oldSize = document.cookie.length;
    tryToClearCookies();
    const newSize = document.cookie.length;
    // eslint-disable-next-line no-alert
    window.alert(
      `Browser-accessible cookies cleared.\n\nOld size: ${oldSize} bytes\nNew size: ${newSize} bytes`
    );

    trackEvent({
      name: ButtonEvents.clearCookies,
      props: { oldSize, newSize, source: "FeaturesTable" },
    });
  }, [trackEvent]);

  /**
   * Resets the FUE (First User Experience) flags for beta testing.
   */
  const resetFueFlags = useCallback(() => {
    trackEvent({
      name: ButtonEvents.resetFueFlags,
      props: { action: "resetFueFlags", source: "FeaturesTable" },
    });

    try {
      dispatch(resetSplashScreenStatus());
      void dispatch(setFueFlags());
      openToast({
        title: "FUE flags reset successfully.",
      });
    } catch (error) {
      throw new Error(`Error resetting FUE flags: ${error}`);
    }
  }, [dispatch, openToast, trackEvent]);

  /**
   * Helper function to send a test error to Sentry and shows a toast notification.
   */
  const sendSentryTestErrorWithToast = useCallback((): void => {
    try {
      const uuid = generateGUID();
      const errorMessage = `${TEST_ERROR_MESSAGE}: ${uuid}`;

      // Send error to Sentry
      sentryCaptureError({
        error: new Error(errorMessage),
        title: TEST_ERROR_MESSAGE,
        data: { testErrorId: uuid },
      });

      // Show success toast with action button to copy UUID
      openToast({
        title: TEST_ERROR_MESSAGE,
        message: `Test error sent to Sentry with the following UUID: ${uuid}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        persist: true,
        action: {
          label: "Copy",
          onClicked: () => {
            copyToClipboard(uuid);
            openToast({
              title: "UUID copied!",
              variant: "info",
            });
          },
        },
      });
    } catch (_error) {
      openToast({
        title: "Failed to send test error to Sentry",
        variant: "error",
      });
    }
  }, [openToast]);

  /**
   * Sends a test error to Sentry with a unique UUID.
   */
  const sendTestSentryError = useCallback(() => {
    // Track the event of sending a test Sentry error
    trackEvent({
      name: ButtonEvents.sendTestSentryError,
      props: { action: "sendTestSentryError", source: "FeaturesTable" },
    });

    const isSentryEnabled = getBooleanQueryParam(
      globalThis.location.search,
      QueryParams.enableSentry
    );

    // The error is not sent when running on localhost.
    if (isLocalhost() && !isSentryEnabled) {
      openToast({
        title: "You can’t send errors to Sentry while running on localhost.",
        variant: "warning",
      });
      return;
    }
    sendSentryTestErrorWithToast();
  }, [sendSentryTestErrorWithToast, openToast, trackEvent]);

  /**
   * Allows sending translation strings to Localize from this environment.
   */
  const allowLocalizeTranslations = useCallback(
    (isEnabled: boolean) => {
      trackEvent({
        name: ButtonEvents.changeLocalizeOverride,
        props: { isEnabled, source: "FeaturesTable" },
      });

      dispatch(setIsTranslationOverrideAllowed(isEnabled));
      updateSearchParams("allowTranslationOverride", isEnabled);
      window.location.reload();
    },
    [dispatch, trackEvent, updateSearchParams]
  );

  /**
   * Toggles dark mode theme on/off.
   */
  const toggleDarkMode = useCallback(
    (isEnabled: boolean) => {
      trackEvent({
        name: ButtonEvents.updateThemeMode,
        props: { isEnabled, source: "FeaturesTable" },
      });

      dispatch(setIsDarkModeEnabled(isEnabled));
      updateSearchParams("enableDarkMode", isEnabled);
    },
    [dispatch, updateSearchParams, trackEvent]
  );

  /**
   * Enables or disables sending errors to Sentry from numbered environments or localhost.
   */
  const enableSentry = useCallback(
    (isEnabled: boolean) => {
      trackEvent({
        name: ButtonEvents.enableSentry,
        props: { isEnabled, source: "FeaturesTable" },
      });

      dispatch(setIsSentryEnabled(isEnabled));
      updateSearchParams("enableSentry", isEnabled);
      window.location.reload();
    },
    [dispatch, updateSearchParams, trackEvent]
  );

  return {
    openAdminPanel,
    clearCookies,
    resetFueFlags,
    sendTestSentryError,
    allowLocalizeTranslations,
    toggleDarkMode,
    enableSentry,
  };
}
